import React, { useEffect, useState } from 'react';

import Tippy from '@tippyjs/react';
import {
  Edit2,
  Trash,
  X,
  ChevronDown,
  ChevronRight,
  EllipsisVertical,
  SaveIcon,
} from 'lucide-react';

import Loader from './Loader';
import Pagination from './Pagination';
import { confirmAction } from '../../utils/sweetAlertUtils';

import 'tippy.js/dist/tippy.css';

interface TableColumn {
  label: string | React.ReactNode;
  key: string;
  format?: (value: any, row: any) => string;
  isImage?: boolean;
  imageSize?: string;
  editable?: boolean;
  width?: string;
  render?: (row: any) => React.ReactElement;
  error?: (row: any) => string | null;
  className?: string;
  editTemplate?: (
    value: any,
    onChange: (val: any) => void,
    row: any,
  ) => React.ReactElement;
}

export interface TableAction {
  label: string;
  onClick: (row: any) => void;
  confirmMessage?: string;
  icon?: React.ReactNode;
  className?: string; 
}

interface TableProps<T = any> {
  columns: TableColumn[];
  data: any[];
  actions?: {
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
    extra?: TableAction[] | ((row: T) => TableAction[]);
  };
  groupBy?: string[]; // New prop for grouping data
  groupHeaderRender?: (group: string, rows: any[]) => React.ReactElement; // Custom group header render
  allowPaging?: boolean;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  isLoading: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  noDataMessage?: string;
  enableInlineEditing?: boolean;
  triggerNewRowEdit?: boolean;
  setTriggerNewRowEdit?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  actions,
  groupBy,
  groupHeaderRender, // Custom group header render prop
  allowPaging = true,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  isLoading,
  noDataMessage = 'No records found',
  enableInlineEditing = false,
  triggerNewRowEdit = false,
  setTriggerNewRowEdit,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [rowEditIndex, setRowEditIndex] = useState<number | null>(null);
  const [editedRow, setEditedRow] = useState<any>({});
  const [dropdownVisible, setDropdownVisible] = useState<number | null>(null);

  useEffect(() => {
    if (enableInlineEditing && data.length > 0 && triggerNewRowEdit) {
      const lastRow = data[data.length - 1];
      setRowEditIndex(lastRow.id || data.length - 1);
      setEditedRow(lastRow);
      setTriggerNewRowEdit?.(false);
    }
  }, [data, triggerNewRowEdit]);

  // Group data by specified fields
  const groupedData = groupBy
    ? data.reduce(
        (acc, row) => {
          const groupKey = groupBy.map((key) => row[key]).join(' - ');
          if (!acc[groupKey]) {
            acc[groupKey] = [];
          }
          acc[groupKey].push(row);

          return acc;
        },
        {} as Record<string, any[]>,
      )
    : { '': data };

  const toggleGroup = (group: string): void => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const toggleDropdown = (index: number): void => {
    setDropdownVisible(dropdownVisible === index ? null : index);
  };

  const closeDropdown = (): void => {
    setDropdownVisible(null);
  };

  const startEditing = (idx: number, row: any): void => {
    if (enableInlineEditing) {
      setRowEditIndex(row.id || idx);
      setEditedRow({ ...row });
    } else if (actions && actions.onEdit) {
      actions.onEdit(row);
    }
  };

  const cancelEditing = (): void => {
    setRowEditIndex(null);
    setEditedRow({});
  };

  const saveEdit = (): void => {
    if (actions && actions.onEdit) {
      actions.onEdit(editedRow);
    }
    setRowEditIndex(null);
    setEditedRow({});
  };

  const handleEditChange = (key: string, value: any): void => {
    setEditedRow((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="">
      <table className="cg-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ width: col.width }}
                className={col.className || ''}
              >
                {col.label}
              </th>
            ))}
            {actions &&
              (actions.onEdit || actions.onDelete || actions.extra) && (
                <th className="w-24">
                  Actions
                </th>
              )}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={columns.length + (actions ? 1 : 0)}
                className="text-center py-6 text-muted font-['Space_Mono'] text-[11px]"
              >
                <Loader />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (actions ? 1 : 0)}
                className="text-center py-6 text-muted font-['Space_Mono'] text-[11px]"
              >
                {noDataMessage}
              </td>
            </tr>
          ) : (
            Object.entries(groupedData).map(([group, rows]) => {
              const typedRows = rows as any[]; // Explicit type assertion

              return (
                <React.Fragment key={group}>
                  {/* Group Header */}
                  {group && (
                    <tr
                      className="cursor-pointer"
                      style={{ background: 'rgba(255,255,255,.03)' }}
                      onClick={() => toggleGroup(group)}
                    >
                      <td
                        colSpan={columns.length + (actions ? 1 : 0)}
                        className="px-0 py-2 font-semibold text-text"
                      >
                        <div className="px-4 flex items-center">
                          {/* Left Section: Chevron */}
                          <span className="mr-2">
                            {collapsedGroups[group] ? (
                              <ChevronRight className="w-4 h-4 text-muted" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-muted" />
                            )}
                          </span>

                          {/* Right Section: Full Flex Container */}
                          <div className="flex flex-1 justify-between items-center">
                            {/* Inject Custom Header */}
                            {groupHeaderRender &&
                              groupHeaderRender(group, typedRows)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Group Rows */}
                  {!collapsedGroups[group] &&
                    typedRows.map((row, idx) => (
                      <tr
                        key={row.id || idx}
                        className="transition-colors"
                      >
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            style={{ width: col.width }}
                            className={col.className || ''}
                          >
                            {rowEditIndex === (row.id || idx) &&
                            col.editable &&
                            enableInlineEditing ? (
                              col.editTemplate ? (
                                col.editTemplate(
                                  editedRow[col.key],
                                  (val) => handleEditChange(col.key, val),
                                  row,
                                )
                              ) : (
                                <input
                                  type="text"
                                  value={editedRow[col.key] || ''}
                                  onChange={(e) =>
                                    handleEditChange(col.key, e.target.value)
                                  }
                                  className="cg-input !h-8 !text-xs"
                                />
                              )
                            ) : col.render ? (
                              col.render(row)
                            ) : col.isImage ? (
                              row[col.key] ? (
                                <img
                                  src={row[col.key]}
                                  alt=""
                                  className="rounded-full"
                                  style={{
                                    width: col.imageSize || '40px',
                                    height: col.imageSize || '40px',
                                  }}
                                />
                              ) : (
                                <div
                                  className="rounded-full bg-[rgba(255,255,255,.1)] text-text flex items-center justify-center"
                                  style={{
                                    width: col.imageSize || '40px',
                                    height: col.imageSize || '40px',
                                  }}
                                >
                                  ?
                                </div>
                              )
                            ) : col.format ? (
                              col.format(row[col.key], row)
                            ) : (
                              row[col.key]
                            )}
                          </td>
                        ))}
                        {actions &&
                          (actions.onEdit ||
                            actions.onDelete ||
                            actions.extra) && (
                            <td className="text-left">
                              <div className="flex space-x-2 justify-start">
                                {enableInlineEditing &&
                                rowEditIndex === (row.id || idx) ? (
                                  <>
                                    <Tippy content="Save">
                                      <button
                                        type="button"
                                        onClick={saveEdit}
                                        className="cg-action-btn bg-[#39b54a] text-white"
                                      >
                                        <SaveIcon className="h-3 w-3" />
                                      </button>
                                    </Tippy>
                                    <Tippy content="Cancel">
                                      <button
                                        type="button"
                                        onClick={cancelEditing}
                                        className="cg-action-btn cg-action-more"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </Tippy>
                                  </>
                                ) : (
                                  <>
                                    {actions.onEdit && (
                                      <Tippy content="Edit">
                                        <button
                                          type="button"
                                          onClick={() => startEditing(idx, row)}
                                          className="cg-action-btn cg-action-edit"
                                        >
                                          <Edit2 className="h-3 w-3" />
                                        </button>
                                      </Tippy>
                                    )}
                                    {actions.onDelete && (
                                      <Tippy content="Delete">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            actions.onDelete?.(row)
                                          }
                                          className="cg-action-btn cg-action-delete"
                                        >
                                          <Trash className="h-3 w-3" />
                                        </button>
                                      </Tippy>
                                    )}
                                    {actions.extra && (
                                      <div className="relative">
                                        <button
                                          onClick={() => toggleDropdown(idx)}
                                          className="cg-action-btn cg-action-more"
                                        >
                                          <EllipsisVertical className="h-4 w-4" />
                                        </button>
                                        {dropdownVisible === idx && (
                                          <div className="absolute right-0 mt-1 min-w-[200px] bg-panel border border-line shadow-xl z-50 overflow-hidden">
                                            {(typeof actions.extra === 'function' 
                                              ? actions.extra(row) 
                                              : actions.extra
                                            ).map((action, index) => (
                                              <button
                                                key={index}
                                                onClick={async () => {
                                                  if (action.confirmMessage) {
                                                    const proceed = await confirmAction({
                                                      title: 'Are you sure?',
                                                      text: action.confirmMessage,
                                                      icon: 'warning',
                                                      confirmButtonText: 'Yes',
                                                      cancelButtonText: 'No',
                                                    });

                                                    if (proceed) {
                                                      action.onClick(row);
                                                    }
                                                  } else {
                                                    action.onClick(row);
                                                  }
                                                  closeDropdown();
                                                }}
                                                className={`flex items-center px-4 py-2 text-[13px] text-text hover:bg-[rgba(255,255,255,.04)] w-full text-left cursor-pointer ${
                                                  action.className || ''
                                                }`}
                                              >
                                                {action.icon && (
                                                  <span className="mr-2 flex-shrink-0">
                                                    {action.icon}
                                                  </span>
                                                )}
                                                <span className="truncate">
                                                  {action.label}
                                                </span>
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                      </tr>
                    ))}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {!isLoading && data.length > 0 && allowPaging && (
        <Pagination
          currentPage={currentPage ?? 0}
          totalPages={totalPages ?? 0}
          onPageChange={onPageChange}
          pageSize={pageSize ?? 0}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default Table;
